import { FC, useCallback, useMemo, useState } from 'react';

import {
  listLoginDeviceLocks,
  releaseAllLoginDeviceLocks,
  releaseLoginDeviceLock,
} from '@shared/api/loginDeviceLock/loginDeviceLock.controller';
import {
  LOGIN_LOCK_TIER_LABEL,
  LoginDeviceLock,
} from '@shared/api/loginDeviceLock/loginDeviceLock.interface';
import { User } from '@shared/api/user/user.interface';
import { Button } from '@shared/components/Button';
import { errorMessageOf } from '@shared/utils/serverErrorMessage.util';
import { Input, Modal, Table, TableColumnsType, Tag, message } from 'antd';
import dayjs from 'dayjs';
import { useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';

import { useAdminTitleHook } from '../hooks/useAdminTitleHook';

/**
 * 로그인 기기 잠금 해제 (전화 응대용).
 * 백엔드 스펙: smartbugo-api/docs/login-device-lock.md
 *
 * "로그인이 안 된다"는 전화를 받았을 때 아이디로 그 사람 기기를 찾아 푸는 화면이다.
 * 잠금의 진실은 서버 문서이므로 사용자가 캐시를 지워도 풀리지 않는다 — 여기서만 풀린다.
 */

const dateText = (value?: Date | string | null): string =>
  value ? dayjs(new Date(value)).format('MM/DD HH:mm') : '-';

const releasedByName = (releasedBy?: User | string): string => {
  if (!releasedBy) return '-';
  if (typeof releasedBy === 'string') return releasedBy;
  return releasedBy.info?.name ?? '-';
};

const AdminLoginDeviceLockPage: FC = function AdminLoginDeviceLockPage() {
  useAdminTitleHook('로그인 잠금 해제');

  //* 계정 관리의 [로그인 잠김] 배지가 ?username= 으로 보내온다 — 그 아이디로 바로 필터
  const [searchParams] = useSearchParams();
  const initialUsername = (searchParams.get('username') ?? '').trim();

  //* 기본은 "지금 잠긴 것만" — 전화 응대의 99%가 이것이다
  const [activeOnly, setActiveOnly] = useState(true);
  const [usernameInput, setUsernameInput] = useState(initialUsername);
  const [username, setUsername] = useState(initialUsername);

  //* 해제 모달 (null이면 닫힘, 'all'이면 전체 해제)
  const [releaseTarget, setReleaseTarget] = useState<LoginDeviceLock | 'all' | null>(
    null,
  );
  const [note, setNote] = useState('');
  const [releasing, setReleasing] = useState(false);

  const { data, status, refetch, isFetching } = useQuery<LoginDeviceLock[]>(
    ['admin/login-device-lock', username, activeOnly],
    () => listLoginDeviceLocks({ username: username || undefined, active: activeOnly }),
    //* 로그인 잠금은 창 포커스마다 다시 부를 만한 화면이 아니다
    { refetchOnWindowFocus: false },
  );

  const rows = useMemo(() => data ?? [], [data]);
  const activeCount = useMemo(() => rows.filter((row) => row.active).length, [rows]);

  const handleSearch = useCallback(() => {
    setUsername(usernameInput.trim());
  }, [usernameInput]);

  const closeRelease = useCallback(() => {
    setReleaseTarget(null);
    setNote('');
  }, []);

  const handleRelease = useCallback(async () => {
    const trimmed = note.trim();
    if (!trimmed) {
      message.warning('해제 사유를 입력해 주세요.');
      return;
    }
    if (!releaseTarget) return;
    setReleasing(true);
    try {
      if (releaseTarget === 'all') {
        const { released } = await releaseAllLoginDeviceLocks(trimmed);
        message.success(`잠금 ${released}건을 해제했습니다.`);
      } else {
        await releaseLoginDeviceLock(releaseTarget._id, trimmed);
        message.success('해제했습니다. 이제 다시 로그인하면 됩니다.');
      }
      closeRelease();
      await refetch();
    } catch (err) {
      message.error(errorMessageOf(err, '해제에 실패했습니다.'));
    } finally {
      setReleasing(false);
    }
  }, [closeRelease, note, refetch, releaseTarget]);

  const columns: TableColumnsType<LoginDeviceLock> = useMemo(
    () => [
      {
        title: '상태',
        key: 'active',
        width: 90,
        render: (_, row) =>
          row.active ? (
            <Tag color="red">잠김</Tag>
          ) : row.releasedAt ? (
            <Tag color="blue">해제됨</Tag>
          ) : (
            <Tag>만료</Tag>
          ),
      },
      {
        title: '시도한 아이디',
        key: 'usernames',
        render: (_, row) =>
          row.usernames.length ? (
            <div className="flex flex-wrap gap-1">
              {row.usernames.map((name) => (
                <Tag key={name} className="m-0">
                  {name}
                </Tag>
              ))}
            </div>
          ) : (
            <span className="text-gray-400">-</span>
          ),
      },
      {
        title: '실패',
        dataIndex: 'failCount',
        key: 'failCount',
        width: 70,
        align: 'right',
      },
      {
        title: '등급',
        key: 'tier',
        width: 90,
        render: (_, row) =>
          row.tier ? LOGIN_LOCK_TIER_LABEL[row.tier] ?? row.tier : '-',
      },
      {
        title: '잠긴 시각',
        key: 'lockedAt',
        width: 110,
        render: (_, row) => dateText(row.lockedAt),
      },
      {
        title: '자동 해제',
        key: 'lockedUntil',
        width: 110,
        render: (_, row) => dateText(row.lockedUntil),
      },
      {
        title: '접속 IP',
        key: 'lastIp',
        width: 130,
        render: (_, row) => row.lastIp ?? '-',
      },
      {
        title: '기기 ID',
        key: 'deviceId',
        width: 120,
        //* 전체는 의미가 없고 앞자리만 있어도 같은 기기인지 대조된다
        render: (_, row) => (
          <span className="font-mono text-12" title={row.deviceId}>
            {row.deviceId.slice(0, 8)}…
          </span>
        ),
      },
      {
        title: '해제 기록',
        key: 'release',
        width: 200,
        render: (_, row) =>
          row.releasedAt ? (
            <div className="text-12 leading-4">
              <div>
                {dateText(row.releasedAt)} · {releasedByName(row.releasedBy)}
              </div>
              {row.releaseNote && (
                <div className="text-gray-500">{row.releaseNote}</div>
              )}
            </div>
          ) : (
            <span className="text-gray-400">-</span>
          ),
      },
      {
        title: '',
        key: 'action',
        width: 90,
        render: (_, row) =>
          row.active ? (
            <Button
              onClick={() => setReleaseTarget(row)}
              className="rounded-md border-red-300 bg-red-50 px-2 py-0.5 text-12 text-red-600"
            >
              해제
            </Button>
          ) : null,
      },
    ],
    [],
  );

  return (
    <div className="p-4 space-y-3">
      <div className="rounded-lg bg-gray-50 px-4 py-3 text-13 leading-5 text-gray-600">
        비밀번호를 여러 번 틀리면 그 기기에서 잠시 로그인할 수 없습니다 (관리자급 5회 · 60분,
        작업자급 10회 · 30분).
        <br />
        시간이 지나면 저절로 풀립니다. 급하면 아이디로 찾아 해제해 주세요 — 해제하면
        실패 횟수도 0이 되고 바로 다시 로그인할 수 있습니다.
      </div>

      {/* antd primary 버튼은 이 프로젝트 테마에서 글자만 흰색으로 나와 안 보인다 — 공용 Button + 명시 색 */}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          className="w-60"
          placeholder="아이디로 검색"
          value={usernameInput}
          onChange={(e) => setUsernameInput(e.target.value)}
          onPressEnter={handleSearch}
          onBlur={handleSearch}
          allowClear
        />
        <Button
          onClick={handleSearch}
          className="rounded-md border-gray-800 bg-gray-800 px-3 py-1 text-13 text-white"
        >
          검색
        </Button>
        <Button
          onClick={() => setActiveOnly((prev) => !prev)}
          className={`rounded-full border px-3 py-1 text-13 ${
            activeOnly
              ? 'border-blue-500 bg-blue-500 text-white'
              : 'border-gray-300 bg-white text-gray-700'
          }`}
        >
          {activeOnly ? '잠긴 것만' : '전체 이력'}
        </Button>
        <Button
          onClick={() => refetch()}
          disabled={isFetching}
          className="rounded-md border-gray-300 bg-white px-3 py-1 text-13 text-gray-700 disabled:opacity-50"
        >
          {isFetching ? '불러오는 중...' : '새로고침'}
        </Button>
        <div className="grow" />
        <Button
          disabled={activeCount === 0}
          onClick={() => setReleaseTarget('all')}
          className="rounded-md border-red-300 bg-red-50 px-3 py-1 text-13 text-red-600 disabled:opacity-40"
        >
          전체 해제{activeCount > 0 ? ` (${activeCount})` : ''}
        </Button>
      </div>

      <Table
        rowKey="_id"
        size="small"
        columns={columns}
        dataSource={rows}
        loading={status === 'loading'}
        pagination={false}
        scroll={{ x: 1100 }}
        locale={{
          emptyText: activeOnly ? '지금 잠긴 기기가 없습니다.' : '기록이 없습니다.',
        }}
      />

      <Modal
        open={releaseTarget !== null}
        title={releaseTarget === 'all' ? '활성 잠금 전체 해제' : '로그인 잠금 해제'}
        onCancel={closeRelease}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              onClick={closeRelease}
              className="rounded-md border-gray-300 bg-white px-3 py-1 text-13 text-gray-700"
            >
              취소
            </Button>
            <Button
              onClick={handleRelease}
              disabled={releasing}
              className="rounded-md border-red-500 bg-red-500 px-3 py-1 text-13 text-white disabled:opacity-50"
            >
              {releasing ? '해제 중...' : '해제'}
            </Button>
          </div>
        }
      >
        {releaseTarget === 'all' ? (
          <p className="mb-3 text-14">
            지금 잠긴 <b>{activeCount}건</b>을 모두 해제합니다. 오작동으로 여러 기기가
            잠겼을 때만 쓰세요.
          </p>
        ) : (
          releaseTarget && (
            <p className="mb-3 text-14">
              시도한 아이디: <b>{releaseTarget.usernames.join(', ') || '-'}</b>
              <br />
              기기 {releaseTarget.deviceId.slice(0, 8)}… 의 잠금을 풉니다.
            </p>
          )
        )}
        <Input.TextArea
          rows={2}
          placeholder="해제 사유 (예: 본인 확인 후 전화 해제)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <p className="mt-2 text-12 text-gray-500">
          누가 왜 풀었는지 기록에 남습니다. 사유는 필수입니다.
        </p>
      </Modal>
    </div>
  );
};

export default AdminLoginDeviceLockPage;
